// src/lib/render/display-escapes.ts
//
// Turns source-code escape sequences that leaked into stored question text
// back into the characters they stand for.
//
// Why this exists: the ACT generator was told to "escape any literal dollar
// sign as \$ (a bare $ is parsed as math)". Nothing on the question surfaces
// parses math — <MathText>, <StemRenderer> and <RichText> render text, not
// KaTeX — so every price showed a literal backslash ("\$85"). The same writer
// sometimes double-escaped its JSON newlines, so explanations stored the two
// characters backslash + n and rendered "Step 1 …\n\nStep 2" on one line
// (QA report: the $85 jacket question, Applied Rates and Percentages).
//
// The renderers apply this before anything else, so old rows and any future
// leak render correctly. The ACT import path and the repair script use the
// same function (via lib/act/content-artifacts.ts), so what is stored and what
// is shown follow ONE rule.
//
// Deliberately narrow: it only rewrites sequences that can never be meant
// literally on a text surface. It does NOT touch LaTeX commands (\times,
// \frac, \neq …) — those are a different defect (raw TeX on a text surface)
// that a mechanical rewrite would get wrong; the content check flags them.

/** LaTeX commands that begin with "\n" — a "\n" followed by one of these is
 *  TeX, not a newline the writer forgot to unescape. */
const TEX_N_COMMANDS = new Set([
  'ne',
  'neq',
  'neg',
  'nabla',
  'nu',
  'ni',
  'not',
  'notin',
  'newline',
  'nmid',
  'ngeq',
  'nleq',
  'nless',
  'ngtr',
  'nexists',
  'nsubseteq',
  'nparallel',
  'noindent',
  'nolimits',
])

/** LaTeX commands that begin with "\t" (\times, \text, \theta …). */
const TEX_T_COMMANDS = new Set([
  'tau',
  'tan',
  'tanh',
  'tbinom',
  'text',
  'textbf',
  'textit',
  'textrm',
  'textsf',
  'texttt',
  'textup',
  'textnormal',
  'textstyle',
  'textdegree',
  'tfrac',
  'theta',
  'therefore',
  'tilde',
  'times',
  'to',
  'top',
  'triangle',
  'triangleq',
  'triangleleft',
  'triangleright',
  'tt',
])

// One pass, left to right, so a rewritten character is never re-scanned. Each
// match starts with the WHOLE run of backslashes before the escape, so "\\$"
// (a TeX line break, or an over-escaped dollar) is seen as one unit and left
// for the content check rather than half-rewritten — which also keeps the
// function idempotent. (A lookbehind would say this more briefly, but it is a
// SyntaxError on older Safari, and this module ships to every question page.)
//   1. \uXXXX (one or two backslashes — JSON escapes that were escaped again)
//   2. a literal "\r\n" pair
//   3. "\n" / "\t" plus the letters that follow (to tell \n from \neq)
//   4. a backslash before a character that never needs one in plain text
const ESCAPE_RE = /(\\+)(?:u([0-9a-fA-F]{4})|(r\\n)|([nt])([A-Za-z]*)|(["'$%&#]))/g

export type DisplayEscapeKind = 'dollar' | 'newline' | 'tab' | 'quote' | 'punct' | 'unicode'

/** Decide one ESCAPE_RE match: what it is and what it becomes, or null when it
 *  must be left alone (TeX command, doubled backslash). */
function decide(
  m: RegExpExecArray | string[],
): { kind: DisplayEscapeKind; replacement: string } | null {
  const [, slashes, hex, crlf, nt, letters, punct] = m
  if (hex !== undefined) {
    return slashes.length <= 2
      ? { kind: 'unicode', replacement: String.fromCharCode(parseInt(hex, 16)) }
      : null
  }
  if (slashes.length !== 1) return null
  if (crlf !== undefined) return { kind: 'newline', replacement: '\n' }
  if (nt !== undefined) {
    const rest = letters ?? ''
    if ((nt === 'n' ? TEX_N_COMMANDS : TEX_T_COMMANDS).has(nt + rest)) return null
    return nt === 'n'
      ? { kind: 'newline', replacement: '\n' + rest }
      : { kind: 'tab', replacement: ' ' + rest }
  }
  if (punct === undefined) return null
  if (punct === '$') return { kind: 'dollar', replacement: '$' }
  if (punct === '"' || punct === "'") return { kind: 'quote', replacement: punct }
  return { kind: 'punct', replacement: punct }
}

/**
 * Rewrite leaked escape sequences in display text:
 *   \$ → $   \% → %   \& → &   \# → #   \" → "   \' → '
 *   \n → newline   \r\n → newline   \t → space   \u221a → √
 * A "\n"/"\t" that starts a LaTeX command (\neq, \times, \text …) is left
 * alone, as is anything behind a doubled backslash. Idempotent: applying it
 * twice equals applying it once.
 */
export function unescapeDisplayText(text: string): string {
  if (!text || !text.includes('\\')) return text
  return text.replace(ESCAPE_RE, (...args: string[]) => {
    const d = decide(args)
    return d ? d.replacement : args[0]
  })
}

/**
 * Every escape unescapeDisplayText would rewrite, with its kind and index — the
 * content check's view of the SAME rule, so what is flagged and what is fixed
 * can never disagree.
 */
export function findDisplayEscapes(text: string): { kind: DisplayEscapeKind; index: number }[] {
  if (!text || !text.includes('\\')) return []
  const out: { kind: DisplayEscapeKind; index: number }[] = []
  const re = new RegExp(ESCAPE_RE.source, 'g')
  for (let m = re.exec(text); m; m = re.exec(text)) {
    const d = decide(m)
    if (d) out.push({ kind: d.kind, index: m.index })
  }
  return out
}

/** True when the text holds an escape that unescapeDisplayText would rewrite. */
export function hasDisplayEscapes(text: string): boolean {
  return findDisplayEscapes(text).length > 0
}
